import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMachine } from '@xstate/react';
import { nanoid } from 'nanoid';
import { AtlasMap } from './components/AtlasMap';
import { ConvictionSlider } from './components/ConvictionSlider';
import { Turnstile } from './components/Turnstile';
import { flowMachine } from './domain/flowMachine';
import { theoryCards, valuePalette } from './domain/theory';
import { getAtlas, getConfig, getDilemma, submitUncertainty } from './lib/api';
import { appendHistory, clearHistory, formatMs, readHistory } from './lib/local';
import { attentionChecksum, getDeviceIdentity, hasConsent, saveConsent } from './lib/device';
import { useAppStore } from './store';
import type { LocalResponse } from './types';

function percent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function describeMirror(local: LocalResponse | null) {
  if (!local) return 'No private mirror yet. Play one dilemma and this becomes yours only.';
  const pull = Math.abs(local.lean);
  const speed = local.latencyMs > 18_000 ? 'slow-burn' : local.latencyMs < 6_000 ? 'spark-fast' : 'measured';
  const motion = local.revisionCount > 8 ? 'restless' : local.revisionCount > 2 ? 'alive' : 'steady';
  const certainty = local.certainty > 0.78 ? 'high conviction' : local.tornness > 0.72 ? 'honest conflict' : 'soft lean';
  return `${speed}, ${motion}, ${certainty}; pull strength ${pull}/100.`;
}

function confidenceBand(certainty: number, tornness: number) {
  if (certainty >= 0.82 && tornness <= 0.33) return 'rock-certain';
  if (tornness >= 0.72) return 'genuinely torn';
  if (certainty <= 0.34) return 'open field';
  return 'leaning';
}

export function App() {
  const [snapshot, send] = useMachine(flowMachine);
  const {
    config,
    device,
    dilemma,
    atlas,
    history,
    loading,
    saving,
    error,
    setConfig,
    setDevice,
    setDilemma,
    setAtlas,
    setHistory,
    setLoading,
    setSaving,
    setError
  } = useAppStore();

  const [consented, setConsented] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [lean, setLeanRaw] = useState(0);
  const [certainty, setCertainty] = useState(0.56);
  const [tornness, setTornness] = useState(0.5);
  const [revisionCount, setRevisionCount] = useState(0);
  const [mirror, setMirror] = useState<{ archetype: string; band: string; observations: string[] } | null>(null);
  const startedAt = useRef<number>(Date.now());
  const lastSubmitted = history.at(-1) ?? null;

  const excludeIds = useMemo(() => history.map((item) => item.dilemmaId), [history]);

  const setLean = useCallback((value: number) => {
    setLeanRaw((previous) => {
      if (previous !== value) setRevisionCount((count) => count + 1);
      return value;
    });
  }, []);

  const refreshAtlas = useCallback(async () => {
    const next = await getAtlas();
    setAtlas(next);
  }, [setAtlas]);

  const loadNext = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      setMirror(null);
      send({ type: 'READY' });
      const next = await getDilemma(excludeIds);
      setDilemma(next);
      setLeanRaw(0);
      setCertainty(0.56);
      setTornness(0.5);
      setRevisionCount(0);
      setTurnstileToken('');
      startedAt.current = Date.now();
      send({ type: 'LOADED' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not load a dilemma';
      setError(message);
      send({ type: 'ERROR' });
    } finally {
      setLoading(false);
    }
  }, [excludeIds, send, setDilemma, setError, setLoading]);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        const [cfg, identity, atlasNow] = await Promise.all([getConfig(), getDeviceIdentity(), getAtlas()]);
        if (cancelled) return;
        setConfig(cfg);
        setDevice(identity);
        setAtlas(atlasNow);
        setHistory(readHistory());
        setConsented(hasConsent(cfg.consentLineVersion));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Boot failed');
      }
    }
    boot();
    return () => { cancelled = true; };
  }, [setAtlas, setConfig, setDevice, setError, setHistory]);

  useEffect(() => {
    if (config && device && !dilemma && !loading) void loadNext();
    if (config && device && !dilemma && loading) void loadNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, device]);

  const turnstileCallback = useCallback((token: string) => setTurnstileToken(token), []);

  const acceptConsent = useCallback((checked: boolean) => {
    setConsented(checked);
    if (checked && config) saveConsent(config.consentLineVersion, config.consentLine);
  }, [config]);

  const onSubmit = useCallback(async () => {
    if (!config || !device || !dilemma) return;
    if (!consented) {
      setError('Please accept the one-line consent before contributing to the commons.');
      return;
    }
    if (!turnstileToken) {
      setError('Complete Turnstile so the dataset stays human.');
      return;
    }

    setSaving(true);
    setError(null);
    send({ type: 'SUBMIT' });
    try {
      const latencyMs = Math.max(0, Date.now() - startedAt.current);
      const distanceFromMidpoint = Math.abs(lean) / 100;
      const convictionDiscordance = Math.min(1, Math.abs(certainty - distanceFromMidpoint));
      const checksum = await attentionChecksum(`${device.deviceHash}:${dilemma.id}:${Math.round(certainty * 100)}:${Math.round(tornness * 100)}:${Math.round(distanceFromMidpoint * 100)}:${Math.min(500, revisionCount)}`);

      const response = await submitUncertainty({
        deviceHash: device.deviceHash,
        dilemmaId: dilemma.id,
        certainty,
        tornness,
        latencyMs,
        revisionCount,
        distanceFromMidpoint,
        convictionDiscordance,
        attentionChecksum: checksum,
        consentLineVersion: config.consentLineVersion,
        turnstileToken
      });

      const local: LocalResponse = {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        dilemmaId: dilemma.id,
        dilemmaTitle: dilemma.title,
        valuePair: dilemma.valuePair,
        lean,
        chosenLabel: lean < 0 ? dilemma.optionA : lean > 0 ? dilemma.optionB : 'Held the middle',
        certainty,
        tornness,
        latencyMs,
        revisionCount,
        distanceFromMidpoint,
        convictionDiscordance,
        mirror: response.mirror.archetype
      };
      setHistory(appendHistory(local));
      setMirror(response.mirror);
      await refreshAtlas();
      send({ type: 'SAVED' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save signal';
      setError(message);
      send({ type: 'ERROR' });
    } finally {
      setSaving(false);
    }
  }, [certainty, config, consented, device, dilemma, lean, refreshAtlas, revisionCount, send, setError, setHistory, setSaving, tornness, turnstileToken]);

  const onClearLocal = useCallback(() => {
    clearHistory();
    setHistory([]);
  }, [setHistory]);

  const localDoubtScore = useMemo(() => {
    if (!history.length) return 0;
    return history.reduce((sum, item) => sum + (item.tornness + item.convictionDiscordance) / 2, 0) / history.length;
  }, [history]);

  const canSubmit = Boolean(config && device && dilemma && consented && turnstileToken && !saving);

  return (
    <main className="appShell">
      <section className="heroPanel">
        <nav className="topbar" aria-label="App status">
          <span className="brandMark">∴</span>
          <span>Doubt Atlas</span>
          <i />
          <span className="pill">device {device?.shortHash ?? 'forming…'}</span>
          <span className="pill">stage {String(snapshot.value)}</span>
          <a className="creatorLink" href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer">LinkedIn</a>
          <a className="creatorLink coffee" href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer">Buy me a coffee</a>
        </nav>

        <div className="heroGrid">
          <div className="heroCopy">
            <p className="eyebrow">A moral-uncertainty instrument disguised as a beautiful little game</p>
            <h1>Don’t tell the machine what you believe. Show it where certainty breaks.</h1>
            <p className="lede">Each round is a balanced dilemma. You keep the side you chose locally. The commons receives only conviction, tornness, hesitation, revision, and value-conflict metadata.</p>
            <div className="consentLine">
              <input
                id="consent"
                type="checkbox"
                checked={consented}
                onChange={(event) => acceptConsent(event.target.checked)}
              />
              <label htmlFor="consent">{config?.consentLine ?? 'I consent to anonymous uncertainty telemetry, never my stance.'}</label>
            </div>
          </div>

          <aside className="privateMirror" aria-label="Private mirror">
            <span className="mirrorGlyph">{lastSubmitted?.mirror ? '◈' : '◇'}</span>
            <h2>Your private mirror</h2>
            <p>{describeMirror(lastSubmitted)}</p>
            <dl>
              <div><dt>Local plays</dt><dd>{history.length}</dd></div>
              <div><dt>Doubt score</dt><dd>{percent(localDoubtScore)}</dd></div>
              <div><dt>Server stores side?</dt><dd>No</dd></div>
            </dl>
          </aside>
        </div>
      </section>

      {error && <div className="toast" role="alert">{error}</div>}

      <section className="playGrid">
        <article className="card dilemmaCard">
          <div className="cardTopline">
            <span>{dilemma?.valuePair ?? 'loading value conflict…'}</span>
            <button className="ghostButton" disabled={loading || saving} onClick={loadNext}>new dilemma</button>
          </div>
          {loading || !dilemma ? (
            <div className="loadingState"><i />Generating a clean conflict…</div>
          ) : (
            <>
              <h2>{dilemma.title}</h2>
              <p className="prompt">{dilemma.prompt}</p>
              <ConvictionSlider lean={lean} setLean={setLean} optionA={dilemma.optionA} optionB={dilemma.optionB} />
              <div className="twoSliders">
                <label>
                  <span>How certain do you feel?</span>
                  <b>{percent(certainty)}</b>
                  <input type="range" min={0} max={100} value={Math.round(certainty * 100)} onChange={(e) => setCertainty(Number(e.target.value) / 100)} />
                </label>
                <label>
                  <span>How genuinely torn are you?</span>
                  <b>{percent(tornness)}</b>
                  <input type="range" min={0} max={100} value={Math.round(tornness * 100)} onChange={(e) => setTornness(Number(e.target.value) / 100)} />
                </label>
              </div>
              <div className="signalPreview">
                <span>{confidenceBand(certainty, tornness)}</span>
                <span>revisions {revisionCount}</span>
                <span>pull distance {percent(Math.abs(lean) / 100)}</span>
              </div>
              {config && <Turnstile siteKey={config.turnstileSiteKey} onToken={turnstileCallback} />}
              <button className="primaryButton" disabled={!canSubmit} onClick={onSubmit}>{saving ? 'saving signal…' : 'commit uncertainty signal'}</button>
            </>
          )}
        </article>

        <aside className="card mirrorCard">
          <h2>Mirror back</h2>
          {mirror ? (
            <div className="mirrorResult">
              <span className="mirrorTitle">{mirror.archetype}</span>
              <p className="band">{mirror.band}</p>
              <ul>
                {mirror.observations.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <button className="primaryButton secondary" onClick={loadNext}>play another</button>
            </div>
          ) : (
            <p>After a response, this panel translates your timing, movement, certainty, and tornness into a metacognitive sketch — private to this browser.</p>
          )}

          <div className="miniHistory">
            <div className="cardTopline"><span>recent local traces</span><button className="ghostButton" onClick={onClearLocal}>clear local</button></div>
            {history.slice(-4).reverse().map((item) => (
              <div className="trace" key={item.id}>
                <strong>{item.dilemmaTitle}</strong>
                <span>{item.chosenLabel} · {formatMs(item.latencyMs)} · {percent(item.certainty)} certain</span>
              </div>
            ))}
          </div>
        </aside>
      </section>

      <section className="commons card" id="stats">
        <div className="sectionHeader">
          <div>
            <p className="eyebrow">public commons</p>
            <h2>{atlas?.oracle.headline ?? 'Loading the commons…'}</h2>
            <p>{atlas?.oracle.detail ?? 'Aggregate maps hide rows until enough people contribute to a value pair.'}</p>
          </div>
          <button className="ghostButton" onClick={() => void refreshAtlas()}>refresh</button>
        </div>
        <AtlasMap rows={atlas?.rows ?? []} totalSignals={atlas?.totalSignals ?? 0} minN={atlas?.minAggregateN ?? 3} />
      </section>

      <section className="theoryGrid" id="help">
        {theoryCards.map((card) => (
          <article className="theoryCard" key={card.name}>
            <h3>{card.name}</h3>
            <p>{card.copy}</p>
          </article>
        ))}
      </section>

      <footer>
        <p>Commons schema: certainty, tornness, latency, revision count, distance-from-middle, value-pair, foundations. Not stored: chosen side, rationale, name, email, account.</p>
        <p>{valuePalette.join(' · ')}</p>
      </footer>
    </main>
  );
}
